import { useState } from 'react';
import { useSearchParams } from 'react-router';
import type { DimensionRow, PageRow, Summary, TimeseriesPoint } from '@totum-analytics/db';
import type { Dimension } from '@totum-analytics/shared';
import { LineChart } from '../charts/charts';
import { DataTable } from '../components/DataTable';
import { RangePicker, useRange } from '../components/RangePicker';
import { Card, ErrorNote, Loading, StatTile, Tabs } from '../components/ui';
import { csvName } from '../lib/csv';
import { formatCount, formatDuration, formatPercent } from '../lib/format';
import { rangeQuery } from '../lib/range';
import { useApi } from '../lib/useApi';
import { useSite } from './SiteLayout';

const SOURCES: { value: Dimension; label: string; header: string }[] = [
  { value: 'referrer', label: 'Referrers', header: 'Referrer' },
  { value: 'utm_source', label: 'UTM source', header: 'Source' },
  { value: 'utm_campaign', label: 'Campaigns', header: 'Campaign' },
  { value: 'country', label: 'Countries', header: 'Country' },
  { value: 'device', label: 'Devices', header: 'Device' },
  { value: 'browser', label: 'Browsers', header: 'Browser' },
  { value: 'os', label: 'OS', header: 'Operating system' },
];

const countryNames = new Intl.DisplayNames(['en-GB'], { type: 'region' });
const valueLabel = (dimension: Dimension, value: string) => {
  if (dimension === 'country' && /^[A-Z]{2}$/.test(value)) return countryNames.of(value) ?? value;
  if (dimension === 'device') return value.charAt(0).toUpperCase() + value.slice(1);
  return value;
};

export function Overview() {
  const { site } = useSite();
  const range = useRange();
  const [params, setParams] = useSearchParams();
  const includeBots = params.get('bots') === '1';
  const [source, setSource] = useState<Dimension>('referrer');
  const q = `${rangeQuery(range)}${includeBots ? '&include_bots=true' : ''}`;
  const base = `/sites/${site.id}/reports`;

  const summary = useApi<Summary>(`${base}/summary?${q}`);
  const series = useApi<TimeseriesPoint[]>(`${base}/timeseries?${q}`);
  const pages = useApi<PageRow[]>(`${base}/pages?${q}&limit=25`);
  const sources = useApi<DimensionRow[]>(`${base}/dimensions/${source}?${q}&limit=25`);

  const cur = summary.data?.current;
  const prev = summary.data?.previous;
  const identified = site.identity_mode === 'identified';
  const sourceHeader = SOURCES.find((s) => s.value === source)?.header ?? 'Value';
  const fileStem = `${site.name}-${range.from}-to-${range.to}`;

  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <RangePicker />
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={includeBots}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.checked) next.set('bots', '1');
              else next.delete('bots');
              setParams(next, { replace: true });
            }}
            className="accent-totum-blue"
          />
          Include bot traffic
        </label>
      </div>

      {summary.error && <ErrorNote error={summary.error} onRetry={summary.reload} />}
      {cur && prev ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatTile label="Pageviews" value={formatCount(cur.pageviews, { compact: true })} current={cur.pageviews} previous={prev.pageviews} />
          {identified ? (
            <StatTile label="Signed-in users" value={formatCount(cur.users)} current={cur.users} previous={prev.users} />
          ) : (
            <StatTile label="Daily visitors, summed" value={formatCount(cur.visitors, { compact: true })} current={cur.visitors} previous={prev.visitors} />
          )}
          <StatTile label="Sessions" value={formatCount(cur.sessions, { compact: true })} current={cur.sessions} previous={prev.sessions} />
          <StatTile
            label="Bounce rate"
            value={formatPercent(cur.bounce_rate)}
            current={cur.bounce_rate ?? undefined}
            previous={prev.bounce_rate ?? undefined}
            upIsGood={false}
          />
          <StatTile
            label="Average session"
            value={formatDuration(cur.avg_session_seconds)}
            current={cur.avg_session_seconds ?? undefined}
            previous={prev.avg_session_seconds ?? undefined}
          />
        </div>
      ) : (
        !summary.error && <Loading label="Loading headline numbers" />
      )}

      <Card title="Traffic by day" className="mb-6">
        {series.error && <ErrorNote error={series.error} onRetry={series.reload} />}
        {series.data ? (
          <LineChart
            label={`Pageviews and visitors per day, ${range.from} to ${range.to}`}
            days={series.data.map((d) => d.day)}
            series={[
              { name: 'Pageviews', color: 'var(--color-series-1)', values: series.data.map((d) => d.pageviews) },
              { name: 'Visitors', color: 'var(--color-series-2)', values: series.data.map((d) => d.visitors) },
            ]}
          />
        ) : (
          <Loading />
        )}
        {!identified && (
          <p className="mt-3 text-xs text-gray-500">
            Visitors are counted per day without cookies, so someone who returns tomorrow counts again. That is the price of
            needing no consent banner; it is described in the planning notes.
          </p>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Top pages">
          {pages.error && <ErrorNote error={pages.error} onRetry={pages.reload} />}
          {pages.data ? (
            <DataTable
              rows={pages.data}
              rowKey={(r) => r.path}
              csvName={csvName(fileStem, 'pages')}
              columns={[
                { header: 'Page', value: (r) => r.path, render: (r) => <span className="font-mono text-xs text-gray-900 break-all">{r.path}</span> },
                { header: 'Views', value: (r) => r.pageviews, render: (r) => formatCount(r.pageviews), align: 'right', bar: true },
                { header: 'Visitors', value: (r) => r.visitors, render: (r) => formatCount(r.visitors), align: 'right' },
                { header: 'Time on page', value: (r) => r.avg_time_seconds, render: (r) => formatDuration(r.avg_time_seconds), align: 'right' },
                { header: 'Scroll', value: (r) => r.avg_scroll_percent, render: (r) => (r.avg_scroll_percent === null ? '—' : `${Math.round(r.avg_scroll_percent)}%`), align: 'right' },
              ]}
            />
          ) : (
            <Loading />
          )}
        </Card>

        <Card title="Where sessions come from" actions={null}>
          <div className="mb-4 overflow-x-auto">
            <Tabs label="Breakdown" value={source} onChange={setSource} options={SOURCES} />
          </div>
          {sources.error && <ErrorNote error={sources.error} onRetry={sources.reload} />}
          {sources.data ? (
            <DataTable
              rows={sources.data}
              rowKey={(r) => r.value}
              csvName={csvName(fileStem, source)}
              columns={[
                { header: sourceHeader, value: (r) => valueLabel(source, r.value), render: (r) => <span className="text-gray-900">{valueLabel(source, r.value)}</span> },
                { header: 'Sessions', value: (r) => r.sessions, render: (r) => formatCount(r.sessions), align: 'right', bar: true },
                { header: 'Visitors', value: (r) => r.visitors, render: (r) => formatCount(r.visitors), align: 'right' },
                { header: 'Pageviews', value: (r) => r.pageviews, render: (r) => formatCount(r.pageviews), align: 'right' },
              ]}
            />
          ) : (
            <Loading />
          )}
        </Card>
      </div>
    </>
  );
}
